import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ScrollView,
    View,
    Text,
    TextInput,
    StyleSheet,
    Platform,
    ActivityIndicator,
} from 'react-native';
import { Redirect, useLocalSearchParams, usePathname, useRouter } from 'expo-router';

import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Banner from '../../../components/ui/Banner';
import ChipInput from '../../../components/ui/ChipInput';
import CategoryPicker from '../../../components/discovery/CategoryPicker';

import { useSession } from '../../../lib/SessionContext';
import { supabase } from '../../../lib/supabase';
import { useProjectMutations } from '../../../lib/hooks/useProjectMutations';
import type { Category } from '../../../lib/database.client.types';
import type { QueryState } from '../../../lib/digestMachine';
import colors from '../../../lib/colors';
import { trackFunnelEvent } from '../../../lib/funnelEvents';
import { buildDashboardPath, DASHBOARD_NEW_ROUTE_PATTERN } from '../../../lib/dashboardRoutes';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { LocalizedMessage } from '../../../lib/i18n/messages';

interface DigestEditorProps {
    projectId?: string;
}

const MIN_RECOMMENDED_INTEREST_LEN = 140;
const MAX_INTEREST_LEN = 2000;

function DigestEditor({ projectId: initialProjectIdProp }: DigestEditorProps = {}) {
    const { i18n } = useLingui();
    const router = useRouter();
    const pathname = usePathname();
    const { session } = useSession();
    const { fromChat, autoSubmit, showProcessing, template, sessionId, data, projectId: legacyProjectId, funnel_journey_id } = useLocalSearchParams<{
        fromChat?: string;
        autoSubmit?: string;
        showProcessing?: string;
        template?: string;
        sessionId?: string;
        data?: string;
        projectId?: string;
        funnel_journey_id?: string;
    }>();

    const fromChatParam = Array.isArray(fromChat) ? fromChat[0] : fromChat;
    const autoSubmitParam = Array.isArray(autoSubmit) ? autoSubmit[0] : autoSubmit;
    const showProcessingParam = Array.isArray(showProcessing) ? showProcessing[0] : showProcessing;
    const templateParam = Array.isArray(template) ? template[0] : template;
    const sessionIdParam = Array.isArray(sessionId) ? sessionId[0] : sessionId;
    const dataParam = Array.isArray(data) ? data[0] : data;
    const legacyProjectIdParam = Array.isArray(legacyProjectId) ? legacyProjectId[0] : legacyProjectId;
    const funnelJourneyIdParam = Array.isArray(funnel_journey_id) ? funnel_journey_id[0] : funnel_journey_id;

    const activeProjectId = initialProjectIdProp || null;
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [chatLoadError, setChatLoadError] = useState<LocalizedMessage | null>(null);
    const hasTrackedLegacyRedirectRef = useRef(false);
    const [formData, setFormData] = useState<QueryState>({
        categories: [],
        interestText: '',
        include: [],
        exclude: [],
    });
    const [isLoadingProjectData, setIsLoadingProjectData] = useState(true);

    const { updateProject, isLoading: isSaving, error: saveError, clearError } = useProjectMutations();

    const hasLegacyOnboardingParams =
        fromChatParam === 'true'
        || autoSubmitParam === 'true'
        || showProcessingParam === 'true'
        || Boolean(templateParam)
        || Boolean(sessionIdParam)
        || Boolean(dataParam)
        || Boolean(legacyProjectIdParam);

    useEffect(() => {
        if (activeProjectId) {
            trackFunnelEvent('form_opened', {
                source: 'edit',
                from_chat: false,
                template: false,
                project_id: activeProjectId,
                funnel_journey_id: funnelJourneyIdParam,
            });
        }
    }, [activeProjectId, funnelJourneyIdParam]);

    useEffect(() => {
        if (!activeProjectId) {
            setIsLoadingProjectData(false);
            return;
        }

        let isActive = true;

        const loadProject = async () => {
            setIsLoadingProjectData(true);

            try {
                const { data: projectData, error } = await supabase
                    .from('project')
                    .select(`
                        interest_description,
                        project_category_interests (
                            category (*)
                        ),
                        project_keywords:project_keyword (*)
                    `)
                    .eq('id', activeProjectId)
                    .is('deleted_at', null)
                    .single();

                if (error) {
                    throw error;
                }

                if (!projectData || !isActive) {
                    return;
                }

                const categories = (projectData.project_category_interests || [])
                    .map((pci: any) => pci.category)
                    .filter(Boolean) as Category[];

                const keywords = projectData.project_keywords || [];
                const include = keywords
                    .filter((keyword: any) => !keyword.is_exclusion)
                    .map((keyword: any) => keyword.keyword);
                const exclude = keywords
                    .filter((keyword: any) => keyword.is_exclusion)
                    .map((keyword: any) => keyword.keyword);

                setFormData({
                    categories,
                    interestText: projectData.interest_description || '',
                    include,
                    exclude,
                });
            } catch (error) {
                console.error('❌ [DigestEditor] Failed to load project data:', error);
                if (!isActive) return;
                    setChatLoadError(msg({ id: 'digestEditor.projectLoadFailure', message: 'We could not load this project. Please go back and try again.' }));
            } finally {
                if (isActive) {
                    setIsLoadingProjectData(false);
                }
            }
        };

        loadProject();

        return () => {
            isActive = false;
        };
    }, [activeProjectId]);

    const canContinue = formData.categories.length > 0;
    const interestLen = formData.interestText.trim().length;
    const hasInterestText = interestLen > 0;
    const tooLongInterest = interestLen > MAX_INTEREST_LEN;
    const tooShortInterest = hasInterestText && interestLen < MIN_RECOMMENDED_INTEREST_LEN;
    const canSave = canContinue && hasInterestText && !tooLongInterest;

    const isCategoryEnabled = true;
    const isInterestsEnabled = canContinue;

    const interestTextLineCount = useMemo(() => {
        if (!formData.interestText) return 5;
        const lineBreaks = (formData.interestText.match(/\n/g) || []).length + 1;
        const estimatedWrapLines = Math.ceil(formData.interestText.length / 60);
        const totalLines = Math.max(lineBreaks, estimatedWrapLines);
        return Math.min(Math.max(totalLines, 5), 10);
    }, [formData.interestText]);

    const handleCategoryAdd = (category: Category) => {
        const exists = formData.categories.some((cat) => cat.id === category.id);
        if (exists) return;
        setFormData((prev) => ({ ...prev, categories: [...prev.categories, category] }));
    };

    const handleCategoryRemove = (categoryId: number) => {
        setFormData((prev) => ({
            ...prev,
            categories: prev.categories.filter((category) => category.id !== categoryId),
        }));
    };

    const handleSave = async () => {
        if (!activeProjectId) {
            return;
        }

        if (!session?.user?.email) {
            setChatLoadError(msg({ id: 'digestEditor.signInRequired', message: 'Please sign in to edit this project.' }));
            return;
        }

        clearError();
        setChatLoadError(null);

        if (!canSave) {
            setChatLoadError(msg({ id: 'digestEditor.requiredFields', message: 'Please complete all required fields before saving.' }));
            return;
        }

        try {
            await updateProject(activeProjectId, formData);
            router.push(buildDashboardPath(pathname, `/${activeProjectId}`));
        } catch (error) {
            console.error('❌ [DigestEditor] Save failed:', error);
        }
    };

    if (!activeProjectId) {
        if (DASHBOARD_NEW_ROUTE_PATTERN.test(pathname || '')) {
            return <Redirect href="/?openChat=true&entrySurface=confirmed_no_topic" />;
        }

        if (hasLegacyOnboardingParams) {
            const destination = templateParam
                ? `/?openChat=true&templateProjectId=${templateParam}`
                : '/?openChat=true';
            if (!hasTrackedLegacyRedirectRef.current) {
                hasTrackedLegacyRedirectRef.current = true;
                trackFunnelEvent('onboarding_form_redirected', {
                    reason: templateParam ? 'legacy_template_redirect' : 'legacy_onboarding_redirect',
                    destination,
                    from_chat: fromChatParam === 'true',
                    template: Boolean(templateParam),
                    ...(templateParam ? { template_project_id: templateParam } : {}),
                    funnel_journey_id: funnelJourneyIdParam,
                });
            }
            if (templateParam) {
                return <Redirect href={destination} />;
            }

            return <Redirect href={destination} />;
        }

        return <Redirect href={buildDashboardPath(pathname)} />;
    }

    return (
        <View style={styles.page}>
            <Header />
            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.container}>
                    <View style={styles.header}>
                        <Text style={styles.h1}><Trans id="digests.new.editResearchProject" comment="UI copy in app/(funnel)/digests/new.tsx">Edit Research Project</Trans></Text>
                        <Text style={styles.sub}><Trans id="digests.new.updateCategoriesInterestsAndKeywordsFeedAndDigestEmailSettings" comment="UI copy in app/(funnel)/digests/new.tsx">
                            Update categories, interests, and keywords. Feed and digest email settings update automatically.
                        </Trans></Text>
                    </View>

                    {(isLoadingProjectData || isSaving) && (
                        <Card>
                            <View style={styles.loadingRow}>
                                <ActivityIndicator size="small" color={colors.primaryGreen} />
                                <Text style={styles.loadingText}>{isLoadingProjectData ? <Trans id="digests.new.loadingProjectSettings" comment="Conditional UI copy in app/(funnel)/digests/new.tsx">Loading project settings...</Trans> : <Trans id="digests.new.savingChanges" comment="Conditional UI copy in app/(funnel)/digests/new.tsx">Saving changes...</Trans>}</Text>
                            </View>
                        </Card>
                    )}

                    {chatLoadError && (
                        <Banner
                            type="warning"
                            title={msg({ id: 'digestEditor.setupNeedsAttention', message: 'Setup needs attention' })}
                            message={chatLoadError}
                            dismissable
                            onDismiss={() => setChatLoadError(null)}
                        />
                    )}

                    {saveError && (
                        <Banner
                            type="error"
                            title={msg({ id: 'digestEditor.failedToSaveProject', message: 'Failed to save project' })}
                            message={saveError}
                            dismissable
                        />
                    )}

                    <View style={isCategoryDropdownOpen ? { zIndex: 10 } : undefined}>
                        <Card style={{ opacity: isCategoryEnabled ? 1 : 0.6 }}>
                            <Text style={styles.cardTitle}><Trans id="digests.new.1ChooseCategories" comment="UI copy in app/(funnel)/digests/new.tsx">1. Choose categories</Trans></Text>
                            <View style={isCategoryDropdownOpen ? { zIndex: 10 } : undefined}>
                                <CategoryPicker
                                    selectedCategories={formData.categories}
                                    onCategoryAdd={handleCategoryAdd}
                                    onCategoryRemove={handleCategoryRemove}
                                    onDropdownChange={setIsCategoryDropdownOpen}
                                    disabled={!isCategoryEnabled}
                                    projectId={activeProjectId}
                                />
                            </View>
                        </Card>
                    </View>

                    <Card style={{ marginTop: 12, opacity: isInterestsEnabled ? 1 : 0.6 }}>
                        <Text style={styles.cardTitle}><Trans id="digests.new.2DescribeInterestsAndTune" comment="UI copy in app/(funnel)/digests/new.tsx">2. Describe interests and tune</Trans></Text>

                        {!isInterestsEnabled && (
                            <Banner
                                type="info"
                                variant="minimal"
                                message={msg({ id: 'digestEditor.selectCategory', message: 'Select at least one category to continue' })}
                            />
                        )}

                        <Text style={styles.label}><Trans id="digests.new.describeYourInterests" comment="UI copy in app/(funnel)/digests/new.tsx">Describe your interests</Trans></Text>
                        <TextInput
                            style={[
                                styles.textArea,
                                { minHeight: interestTextLineCount * 24 + 24 },
                            ]}
                            multiline
                            placeholder={i18n._(msg({
                                id: 'digestEditor.interestsPlaceholder',
                                message: 'Two sentences about what you care about (not a query)...',
                            }))}
                            placeholderTextColor={colors.mediumGray}
                            value={formData.interestText}
                            onChangeText={(value) => setFormData((prev) => ({ ...prev, interestText: value }))}
                            editable={isInterestsEnabled}
                            testID="digest-interest-text-input"
                        />
                        <Text style={[styles.tip, (tooShortInterest || tooLongInterest) ? styles.tipWarn : null]}><Trans id="digests.new.tipAimFor12SentencesRecommendedCharsMax" comment="UI copy in app/(funnel)/digests/new.tsx">
                            Tip: Aim for 1-2 sentences (recommended: {MIN_RECOMMENDED_INTEREST_LEN}+ chars, max: {MAX_INTEREST_LEN}). Current length: {interestLen}.
                        </Trans></Text>

                        <View style={styles.grid2}>
                            <View style={styles.gridItem}>
                                <ChipInput
                                    label={msg({ id: 'digestEditor.boostKeywords', message: '+ Keywords (boost)' })}
                                    value={formData.include}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, include: value }))}
                                    placeholder={msg({
                                        id: 'digestEditor.boostPlaceholder',
                                        message: 'machine learning, neural networks...',
                                    })}
                                    tip={msg({
                                        id: 'digestEditor.boostTip',
                                        message: 'Add specialized terms that reflect your research focus or expertise.',
                                    })}
                                    disabled={!isInterestsEnabled}
                                />
                            </View>
                            <View style={styles.gridItem}>
                                <ChipInput
                                    label={msg({ id: 'digestEditor.excludeKeywords', message: '- Keywords (exclude)' })}
                                    value={formData.exclude}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, exclude: value }))}
                                    placeholder={msg({ id: 'digestEditor.excludePlaceholder', message: 'review, tutorial...' })}
                                    tip={msg({
                                        id: 'digestEditor.excludeTip',
                                        message: 'Papers containing these keywords in titles or abstracts will be excluded.',
                                    })}
                                    disabled={!isInterestsEnabled}
                                />
                            </View>
                        </View>
                    </Card>

                    <Card style={{ marginTop: 12, marginBottom: 60 }}>
                        <View style={styles.buttonRow}>
                            <Button
                                label={isSaving
                                    ? msg({ id: 'digestEditor.saving', message: 'Saving...' })
                                    : msg({ id: 'digestEditor.saveProject', message: 'Save project' })}
                                variant="primary"
                                disabled={!canSave || isSaving || isLoadingProjectData}
                                onPress={handleSave}
                                testID="digest-save-button"
                            />
                            <Button
                                label={msg({ id: 'common.cancel', message: 'Cancel' })}
                                variant="secondary"
                                disabled={isSaving}
                                onPress={() => router.push(buildDashboardPath(pathname, `/${activeProjectId}`))}
                            />
                        </View>
                    </Card>
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

export default DigestEditor;

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    container: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
    },
    scrollContent: {
        flexGrow: 1,
    },
    header: {
        paddingTop: 8,
        paddingBottom: 16,
    },
    h1: {
        fontSize: 22,
        fontWeight: '800',
        color: '#0f172a',
        marginRight: 12,
    },
    sub: {
        fontSize: 12,
        color: '#64748b',
        marginTop: 6,
    },
    cardTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: 16,
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: '#334155',
        marginBottom: 6,
    },
    textArea: {
        backgroundColor: '#ffffff',
        borderColor: '#cbd5e1',
        borderWidth: 1,
        borderRadius: 10,
        paddingVertical: 12,
        paddingHorizontal: 12,
        fontSize: 16,
        color: '#0f172a',
    },
    tip: {
        fontSize: 12,
        color: '#64748b',
        marginTop: 6,
    },
    tipWarn: {
        color: colors.errorRed,
    },
    grid2: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 16,
    },
    gridItem: {
        flex: 1,
        minWidth: 280,
    },
    buttonRow: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        gap: 12,
    },
    loadingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    loadingText: {
        fontSize: 14,
        color: colors.text700,
    },
});
